import emojiData from '@emoji-mart/data';
import EmojiPicker from '@emoji-mart/react';
import { ArrowUpTrayIcon, FaceSmileIcon, PlusIcon } from '@heroicons/react/24/outline';
import { PaperAirplaneIcon } from '@heroicons/react/24/solid';
import clsx from 'clsx';
import type React from 'react';
import { Fragment, Suspense, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

import { ErrorBoundary } from '@/components/ErrorBoundary';
import { useIsMounted } from '@/renderer/utils/hooks/useIsMounted';
import { IS_MOBILE_BUILD } from '@/renderer/utils/isMobileBuild';
import useMe, { authToken } from '@/renderer/utils/useMe';
import useRequest from '@/renderer/utils/useRequest';
import type { RequestSchema } from '@/types/request';
import CopyCryptoPaymentButton from './CopyCryptoPaymentButton';
import CopyPendingPlaysButton from './CopyPendingPlaysButton';
import ImageAttachment from './ImageAttachment';
import { typingRequest, useTypingContext } from './TypingContext';

const messageCreateRequest: RequestSchema<
  API.DirectMessageReceipt,
  API.WithData<{
    content: string;
    receiverUserId: API.Id | '0';
    type: 'dm' | 'dmh';
    // #1808 — public R2 URL of a single attached image, when present.
    imageUrl?: string;
    // Staff-only: false mutes the fallback "unread reply" email for this message
    // (a wind-down closer). Omitted/true escalates substantive replies (#1720).
    notify?: boolean;
  }>
> = {
  name: 'messageCreateRequest',
  input: '/api/v1/message/create',
  method: 'POST',
};

const API_BASE =
  (import.meta.env.VITE_PUBLIC_ENV__API_URL as string) ||
  (typeof location !== 'undefined' ? location.origin : '');

const MAX_IMAGE_BYTES = 20 * 1024 * 1024;

/**
 * Upload a single support-chat image to R2 via the authenticated endpoint and
 * return its public URL (#1808). Unlike the web-only author editor's cookie
 * upload, support chat also runs in the native app where cookies don't persist,
 * so this attaches the Bearer token exactly like `renderer/utils/api.ts` does
 * (the stored token is JSON-quoted in localStorage).
 */
async function uploadSupportImage(file: File): Promise<string> {
  if (!file.type.startsWith('image/')) {
    throw new Error('Please choose an image file.');
  }
  if (file.size > MAX_IMAGE_BYTES) {
    throw new Error('Image must be under 20MB.');
  }

  const rawToken = typeof window !== 'undefined' ? authToken() : undefined;
  let bearer: string | null | undefined = rawToken;
  if (typeof rawToken === 'string' && rawToken.startsWith('"')) {
    try {
      bearer = JSON.parse(rawToken);
    } catch {
      // Fall back to the raw value if it isn't valid JSON.
    }
  }

  const res = await fetch(`${API_BASE}/api/v1/message/upload`, {
    method: 'POST',
    credentials: 'include',
    headers: {
      'Content-Type': file.type,
      ...(bearer ? { Authorization: `Bearer ${bearer}` } : {}),
    },
    body: file,
  });

  if (!res.ok) {
    const msg = await res
      .json()
      .then((j) => (j as { message?: string })?.message)
      .catch(() => null);
    throw new Error(msg || `Upload failed (${res.status})`);
  }

  return ((await res.json()) as { url?: string }).url as string;
}

type Attachment = {
  id: number;
  previewUrl: string;
  status: 'uploading' | 'done' | 'error';
  url?: string;
  error?: string;
  // Set once the served (R2) image has actually decoded (#1822). Until then the
  // local object-URL preview stays on screen, so swapping to the served <img>
  // never leaves a blank frame (the flash between spinner and finished upload).
  uploadedLoaded?: boolean;
};

/** Pull the first image File out of a paste/drop transfer, if any. */
function imageFileFrom(list: FileList | null | undefined): File | null {
  if (!list) {
    return null;
  }
  for (const file of Array.from(list)) {
    if (file.type.startsWith('image/')) {
      return file;
    }
  }
  return null;
}

export default function MessageInput({
  user,
  chatId = 'me',
}: {
  user: API.User | undefined;
  chatId: API.Id | 'me';
}) {
  const me = useMe();
  const meId = me?.id;
  // Only staff/admin ever see a "viewing" indicator. A support-chat user is shown
  // staff typing but must never see that staff opened/viewed the chat (#1715).
  const isStaffViewer = me?.role === 'admin' || me?.role === 'staff';
  const chatIdNumber = chatId === 'me' ? meId || 0 : chatId || 0;

  const inputRef = useRef<HTMLSpanElement | null>(null);
  const sendTypingRequest = useRequest(typingRequest, { manual: true, hideAlert: true });
  const sendMessageRequest = useRequest(messageCreateRequest, { manual: true });
  const cachedMessages = useRef<Map<typeof chatId, string>>(new Map());
  // Never autofocus inside the packaged native app: focusing the composer on
  // mount pops the soft keyboard immediately, which on the native WebView
  // shrinks the visual viewport mid-render and leaves the composer floating high
  // with the messages jammed above it until the layout settles (#1579). On web
  // the keyboard is user-initiated, so members still get the convenience.
  const autoFocus = !IS_MOBILE_BUILD && user?.role !== 'admin' && user?.role !== 'staff';

  useEffect(() => {
    sendTypingRequest.run(
      {
        data: {
          type: 'dm',
          len: inputRef.current?.innerText?.trim?.().length || 0,
        },
      },
      {
        req: { input: `/api/v1/messageReceipt/realtime/typing/${chatIdNumber}` },
        dontStoreError: true,
        dontAlterState: true,
      },
    );
  }, [sendTypingRequest, chatIdNumber]);

  const isMounted = useIsMounted();
  const isRunning = useRef(false);

  // Staff-only, per-message: when messaging a specific member, staff can mute the
  // fallback "unread reply" email for a wind-down closer (#1720). Defaults ON
  // (substantive replies escalate) and resets to ON after every send so a mute
  // is a deliberate one-off. The member's own composer never sends this.
  const canMuteEmail = isStaffViewer && chatId !== 'me';
  const [emailIfUnread, setEmailIfUnread] = useState(true);

  const [inputError, setInputError] = useState('');
  const [isEmojiPickerOpen, setIsEmojiPickerOpen] = useState(false);

  // ── Image attachment state (#1808) ───────────────────────────────────────
  const [attachment, setAttachment] = useState<Attachment | null>(null);
  // Track whether the contentEditable has text so send can enable for an
  // image-only message and disable while an upload is in flight.
  const [hasText, setHasText] = useState(false);
  const [plusMenuOpen, setPlusMenuOpen] = useState(false);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const plusRef = useRef<HTMLDivElement | null>(null);
  // The "+" menu is portaled to <body> (below) so it can't be clipped by the
  // composer's overflow near a window edge — same reason the toolbar popovers use
  // Headless UI `anchor`. This ref is the portaled panel (for the outside-click
  // check), and this state is its fixed viewport position, flipped above/below the
  // button based on available room.
  const plusMenuRef = useRef<HTMLDivElement | null>(null);
  const [plusMenuPos, setPlusMenuPos] = useState<{
    left: number;
    top?: number;
    bottom?: number;
  } | null>(null);
  const plusClickTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const attachmentIdSeq = useRef(0);

  const clearAttachment = () => {
    setAttachment((prev) => {
      if (prev?.previewUrl) {
        URL.revokeObjectURL(prev.previewUrl);
      }
      return null;
    });
  };

  const startUpload = (file: File) => {
    if (!file.type.startsWith('image/')) {
      setInputError('Please choose an image file.');
      return;
    }
    setInputError('');
    // Single image for the first cut: a new pick replaces any prior one.
    const previewUrl = URL.createObjectURL(file);
    const id = ++attachmentIdSeq.current;
    setAttachment((prev) => {
      if (prev?.previewUrl) {
        URL.revokeObjectURL(prev.previewUrl);
      }
      return { id, previewUrl, status: 'uploading' };
    });

    uploadSupportImage(file)
      .then((url) => {
        setAttachment((prev) => (prev?.id === id ? { ...prev, status: 'done', url } : prev));
      })
      .catch((err: Error) => {
        setAttachment((prev) =>
          prev?.id === id ? { ...prev, status: 'error', error: String(err.message || err) } : prev,
        );
      });
  };

  const openFilePicker = () => {
    setPlusMenuOpen(false);
    fileInputRef.current?.click();
  };

  // Clean up any lingering object URL / timer on unmount.
  useEffect(() => {
    return () => {
      if (plusClickTimer.current) {
        clearTimeout(plusClickTimer.current);
      }
      setAttachment((prev) => {
        if (prev?.previewUrl) {
          URL.revokeObjectURL(prev.previewUrl);
        }
        return prev;
      });
    };
  }, []);

  // Position the portaled "+" menu when it opens: anchor it to the button, opening
  // above when there's room (matching the toolbar popovers) and flipping below when
  // there isn't, and clamp it to the viewport so it never runs off the right edge.
  useEffect(() => {
    if (!plusMenuOpen || !plusRef.current) {
      setPlusMenuPos(null);
      return;
    }
    const rect = plusRef.current.getBoundingClientRect();
    const gap = 8;
    const menuWidth = 240; // min-w-[15rem]
    const estMenuHeight = 56;
    const openAbove = rect.top > estMenuHeight + gap;
    const maxLeft = window.innerWidth - menuWidth - 8;
    const left = Math.max(8, Math.min(rect.left, maxLeft));
    setPlusMenuPos(
      openAbove
        ? { left, bottom: window.innerHeight - rect.top + gap }
        : { left, top: rect.bottom + gap },
    );
  }, [plusMenuOpen]);

  // Close the "+" menu on an outside click, Escape, or a scroll/resize (the menu is
  // position:fixed, so it would otherwise drift away from the button).
  useEffect(() => {
    if (!plusMenuOpen) {
      return;
    }
    const onDocMouseDown = (event: MouseEvent) => {
      const target = event.target as Node;
      const inButton = plusRef.current?.contains(target);
      const inMenu = plusMenuRef.current?.contains(target);
      if (!inButton && !inMenu) {
        setPlusMenuOpen(false);
      }
    };
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setPlusMenuOpen(false);
      }
    };
    const onScrollOrResize = () => setPlusMenuOpen(false);
    document.addEventListener('mousedown', onDocMouseDown);
    document.addEventListener('keydown', onKey);
    window.addEventListener('scroll', onScrollOrResize, true);
    window.addEventListener('resize', onScrollOrResize);
    return () => {
      document.removeEventListener('mousedown', onDocMouseDown);
      document.removeEventListener('keydown', onKey);
      window.removeEventListener('scroll', onScrollOrResize, true);
      window.removeEventListener('resize', onScrollOrResize);
    };
  }, [plusMenuOpen]);

  const submit = async (input: HTMLSpanElement, cId: API.Id | 'me') => {
    const text = input.innerText;
    const content = text.trim();
    const imageUrl = attachment?.status === 'done' ? attachment.url : undefined;

    // Nothing to send, an upload is still resolving, or a send is already in
    // flight — bail. An image-only message (no text) is allowed once uploaded.
    if (attachment?.status === 'uploading' || isRunning.current) {
      return;
    }
    if (!content && !imageUrl) {
      return;
    }

    isRunning.current = true;

    try {
      await sendMessageRequest.run(
        {
          data: {
            content,
            receiverUserId: cId === 'me' ? '0' : cId,
            type: 'dm',
            ...(imageUrl ? { imageUrl } : {}),
            ...(canMuteEmail && !emailIfUnread ? { notify: false } : {}),
          },
        },
        { throwOnRun: true },
      );

      if (input.innerText === text) {
        input.innerText = '';
      } else if (input.innerText.startsWith(text)) {
        input.innerText = input.innerText.substring(text.length);
      }
      setHasText(!!input.innerText.trim());

      // Drop the attachment once the message is sent.
      clearAttachment();

      // Re-arm the email fallback for the next reply.
      if (canMuteEmail) {
        setEmailIfUnread(true);
      }

      isRunning.current = false;
    } catch (error) {
      isRunning.current = false;

      throw error;
    }
  };

  useEffect(() => {
    if (inputRef.current) {
      const text = cachedMessages.current.get(chatId);

      if (text) {
        inputRef.current.innerText = text;
        setHasText(!!text.trim());
        cachedMessages.current.delete(chatId);
      }
    }

    return () => {
      if (inputRef.current?.innerText.trim()) {
        cachedMessages.current.set(chatId, inputRef.current.innerText);
        inputRef.current.innerText = '';
      } else {
        cachedMessages.current.delete(chatId);
      }
    };
  }, [chatId]);

  useEffect(() => {
    if (autoFocus) {
      inputRef.current?.focus();
    }
  }, [autoFocus]);

  const { state: typingState } = useTypingContext();

  const [chatStatusTyping, chatStatusViewing] = useMemo(() => {
    const typing: { name: string; id: API.Id }[] = [];
    const viewing: { name: string; id: API.Id }[] = [];

    for (const [activityUserId, activity] of typingState.byChatId[chatIdNumber]?.users
      ? Object.entries(typingState.byChatId[chatIdNumber].users)
      : []) {
      if (activity && meId && +activityUserId !== +meId) {
        if (activity.status === 'TYPE') {
          typing.push({ name: activity.name, id: +activityUserId });
        } else if (activity.status === 'VIEW' && isStaffViewer) {
          viewing.push({ name: activity.name, id: +activityUserId });
        }
      }
    }

    return [typing, viewing];
  }, [typingState, chatIdNumber, meId, isStaffViewer]);

  const sendTypingTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const isUploading = attachment?.status === 'uploading';
  const canSend = (hasText || attachment?.status === 'done') && !isUploading;

  const handleSend = () => {
    if (inputRef.current) {
      setInputError('');
      submit(inputRef.current, chatId).catch((err: Error) => setInputError(String(err)));
    }
  };

  // Drop a pre-built snippet (pending plays, crypto pay instructions) straight into
  // the draft instead of routing it through the clipboard (#1817 follow-up). Appends
  // to whatever the admin has already typed — a newline separator when the draft is
  // non-empty so a snippet never runs into the prior line — then puts the caret at
  // the end and focuses the input. Returns false if the input isn't mounted yet, so
  // the caller can fall back to a clipboard copy.
  const insertIntoComposer = (text: string): boolean => {
    const input = inputRef.current;
    if (!input || !text) {
      return false;
    }

    // Strip trailing whitespace/newlines first: an "empty" contentEditable can report
    // innerText as "\n" (a placeholder <br>), which would otherwise prepend a blank
    // line above the snippet. Only a draft with real content gets a newline separator.
    const existing = input.innerText.replace(/\s+$/, '');
    input.innerText = existing ? `${existing}\n${text}` : text;
    setHasText(!!input.innerText.trim());

    // Focus and drop the caret at the very end so the admin can keep typing after it.
    input.focus();
    const range = document.createRange();
    range.selectNodeContents(input);
    range.collapse(false);
    const selection = window.getSelection();
    if (selection) {
      selection.removeAllRanges();
      selection.addRange(range);
    }

    return true;
  };

  return (
    // Bottom padding is gated by DEVICE TYPE, not viewport width: the tight "hug the
    // bottom" treatment is a touch-only thing (it looked odd on a narrow desktop
    // window, which a width breakpoint like `md:` couldn't distinguish from a phone).
    // - Default (hover/pointer devices, i.e. desktop at any width): a fixed ~1.5rem so
    //   the composer isn't jammed against the edge.
    // - `no-hover` (touch devices): the keyboard-aware hug (#1831/#1839) —
    //   0.25rem + (safe-area inset × keyboard-closed):
    //     • Keyboard closed: 0.25rem + inset — hugs the bottom while clearing the home
    //       indicator (#1477), the flush native feel from #1831.
    //     • Keyboard open: the inset collapses to 0, so it sits a hair above the
    //       keyboard with no dead gap below it.
    //   The keyboard state comes from the shared `--support-kb-closed` / `--sa-bottom`
    //   contract (#1579/#1755), driven only on the native shell (feature/android-
    //   platform); on the mobile web they're undefined, so the fallback resolves to
    //   `0.25rem + env(safe-area-inset-bottom)` (unchanged mobile-web behaviour).
    //
    // Both subtract `--tabbar-space`, because the mobile tab bar below already
    // spans the bottom inset and the composer must not pay for it twice. Without
    // that, the composer cleared the home indicator, and then the bar cleared it
    // again underneath — an empty band between the two, and two bottom bars
    // instead of one docked assembly. `max(0px, …)` keeps it from going negative
    // once the bar is taller than the inset, which it always is when present.
    // `--tabbar-space` is 0 on desktop, wherever no bar is mounted, and while the
    // keyboard is up, so every case above is otherwise unchanged.
    <div className="p-4 pt-0 pb-[calc(1.5rem+max(0px,var(--safe-bottom)-var(--tabbar-space)))] no-hover:pb-[calc(0.25rem+max(0px,var(--safe-bottom)*var(--support-kb-closed,1)-var(--tabbar-space)))]">
      {/* Typing / viewing status (#1715/#1716). The staff admin actions used to
          float on this row and could overlap this text; they now live in the admin
          toolbar attached to the top of the input below (#1817), so this row is the
          status text alone. */}
      <div className="p-1">
        <div className="select-none text-xs">
          {chatStatusTyping?.map((activity, idx) => (
            <Fragment key={activity.id}>
              <span className="font-semibold">
                {idx > 0 ? (idx === chatStatusTyping.length - 1 ? ' & ' : ', ') : ''}
                {activity.name}
              </span>
            </Fragment>
          ))}
          {chatStatusTyping.length > 1
            ? ' are typing'
            : chatStatusTyping.length
              ? ' is typing'
              : ''}
          {!!chatStatusTyping.length && !!chatStatusViewing.length
            ? ' and '
            : chatStatusTyping.length
              ? '...'
              : ''}
          {chatStatusViewing?.map((activity, idx) => (
            <Fragment key={activity.id}>
              {idx > 0 ? (idx === chatStatusViewing.length - 1 ? ' & ' : ', ') : ''}
              <span className="font-semibold">{activity.name}</span>
            </Fragment>
          ))}
          {chatStatusViewing.length > 1
            ? ' are viewing.'
            : chatStatusViewing.length
              ? ' is viewing.'
              : ''}
          &nbsp;
        </div>
      </div>
      {!!inputError && (
        <div className="mb-1 px-1 text-xs text-red-500 dark:text-red-400">{inputError}</div>
      )}
      {attachment?.status === 'error' && (
        <div className="mb-1 px-1 text-xs text-red-500 dark:text-red-400">
          {attachment.error || 'Image upload failed.'}
        </div>
      )}
      <div className="flex flex-row items-start space-x-3">
        {/* The bordered composer is a column: the input row on top, the image
            preview thumbnail beneath the text (#1808). */}
        <div className="pointer-events-none flex w-full grow flex-col rounded-md border-0 px-3 py-1.5 text-base shadow-xs outline outline-1 -outline-offset-1 outline-zinc-900/20 focus-within:outline-zinc-900/50 dark:bg-white/5 dark:outline-white/10 dark:focus-within:outline-white/20 sm:text-sm">
          {isStaffViewer && (
            // Admin toolbar (#1817): a gray strip flush with the top edge of the
            // input, Slack-style, holding the staff-only composer actions left-aligned
            // (#1825) — Copy pending plays (#1758), Copy BTC/ETH pay (#1803), and the
            // per-message "Email if unread" mute (#1720). It bleeds to the box edges
            // with negative margins and re-enables pointer events (the composer box
            // itself is pointer-events-none). The composer's outline is a real CSS
            // outline (painted on top of children), so this gray strip sits behind it
            // rather than covering it (#1825). Members never see it; their composer is
            // unchanged.
            <div className="-mx-3 -mt-1.5 pointer-events-auto mb-2 flex flex-wrap items-center justify-start gap-x-3 gap-y-1 rounded-t-md border-zinc-900/10 border-b bg-zinc-100 px-3 py-1.5 dark:border-white/10 dark:bg-white/10">
              <CopyPendingPlaysButton onInsert={insertIntoComposer} />
              <CopyCryptoPaymentButton onInsert={insertIntoComposer} />
              {canMuteEmail && (
                <label className="flex cursor-pointer select-none items-center gap-1.5 text-xs text-zinc-500 dark:text-zinc-400">
                  <input
                    type="checkbox"
                    checked={emailIfUnread}
                    onChange={(event) => setEmailIfUnread(event.target.checked)}
                    className="h-3.5 w-3.5 rounded border-zinc-300 text-emerald-500 focus:ring-emerald-500 dark:border-zinc-600"
                  />
                  Email if unread
                </label>
              )}
            </div>
          )}
          <div className="flex w-full flex-row items-end space-x-2">
            {/* "+" attach button (#1808) — Slack-style: single click opens a small
                menu, double click opens the file picker directly. The + rotates
                135° into an ✕ when the menu opens (no spin under reduced motion). */}
            <div ref={plusRef} className="relative">
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                className="hidden"
                onChange={(event) => {
                  const file = event.target.files?.[0];
                  if (file) {
                    startUpload(file);
                  }
                  // Allow re-selecting the same file later.
                  event.target.value = '';
                }}
              />
              <button
                type="button"
                title="Attach"
                aria-label="Attach"
                aria-expanded={plusMenuOpen}
                className="pointer-events-auto flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-zinc-400 hover:bg-zinc-200 hover:text-zinc-700 dark:text-zinc-500 dark:hover:bg-white/10 dark:hover:text-zinc-200"
                onClick={() => {
                  // Defer the single-click toggle so a double-click can pre-empt
                  // it and open the picker directly (Slack's double-click upload).
                  if (plusClickTimer.current) {
                    clearTimeout(plusClickTimer.current);
                  }
                  plusClickTimer.current = setTimeout(() => {
                    setPlusMenuOpen((open) => !open);
                    plusClickTimer.current = null;
                  }, 220);
                }}
                onDoubleClick={() => {
                  if (plusClickTimer.current) {
                    clearTimeout(plusClickTimer.current);
                    plusClickTimer.current = null;
                  }
                  openFilePicker();
                }}
              >
                <PlusIcon
                  className={clsx(
                    'h-5 w-5 transition-transform duration-200 ease-[cubic-bezier(0.34,1.56,0.64,1)] motion-reduce:transition-none',
                    plusMenuOpen && 'rotate-[135deg]',
                  )}
                  aria-hidden="true"
                />
              </button>
              {plusMenuOpen &&
                plusMenuPos &&
                typeof document !== 'undefined' &&
                createPortal(
                  <div
                    ref={plusMenuRef}
                    style={{
                      position: 'fixed',
                      left: plusMenuPos.left,
                      top: plusMenuPos.top,
                      bottom: plusMenuPos.bottom,
                    }}
                    className="pointer-events-auto z-50 min-w-[15rem] overflow-hidden rounded-lg bg-white py-1 shadow-lg ring-1 ring-zinc-900/10 dark:bg-zinc-800 dark:ring-white/10"
                  >
                    <button
                      type="button"
                      className="flex w-full items-center gap-3 px-3 py-2 text-left text-sm text-zinc-700 hover:bg-zinc-100 dark:text-zinc-200 dark:hover:bg-white/10"
                      onClick={openFilePicker}
                    >
                      <ArrowUpTrayIcon
                        className="size-5 shrink-0 text-zinc-500"
                        aria-hidden="true"
                      />
                      Upload from your computer
                    </button>
                  </div>,
                  document.body,
                )}
            </div>

            <ErrorBoundary>
              <Suspense>
                <div
                  onMouseDown={(event) => {
                    event.stopPropagation();

                    if ((event.target as HTMLDivElement)?.nodeName === 'EM-EMOJI-PICKER') {
                      const target = event.target as HTMLDivElement;

                      // Do a hacky X Y check to see if they clicked on the search box
                      if (
                        event.clientX - target.offsetLeft >= 14 &&
                        event.clientX - target.offsetLeft <= 265 &&
                        event.clientY - target.offsetTop >= 54 &&
                        event.clientY - target.offsetTop <= 85
                      ) {
                        // Allow the click onto the search box
                      } else {
                        event.preventDefault();
                      }
                    } else {
                      event.preventDefault();
                    }
                  }}
                >
                  <FaceSmileIcon
                    className="pointer-events-auto h-6 w-6 shrink-0 cursor-pointer rounded-full text-zinc-400 hover:rotate-12 hover:bg-yellow-300 hover:text-zinc-900 dark:text-zinc-500"
                    onClick={(event) => {
                      event.stopPropagation();
                      event.preventDefault();
                      setIsEmojiPickerOpen(!isEmojiPickerOpen);
                    }}
                  />
                  {isEmojiPickerOpen && (
                    <EmojiPicker
                      data={emojiData}
                      onClickOutside={(event: React.MouseEvent<Document, MouseEvent>) => {
                        setIsEmojiPickerOpen(false);
                        event.stopPropagation();
                      }}
                      onEmojiSelect={(event: { native: string }) => {
                        const text = event.native;
                        const isInputActive = document.activeElement === inputRef.current;

                        if (isInputActive && document.queryCommandSupported?.('insertText')) {
                          document.execCommand('insertText', false, text);
                        } else {
                          const range = isInputActive && document.getSelection()?.getRangeAt(0);

                          if (range) {
                            const textNode = document.createTextNode(text);

                            range.deleteContents();
                            range.insertNode(textNode);
                            range.selectNodeContents(textNode);
                            range.collapse(false);

                            const selection = window.getSelection();

                            if (selection) {
                              selection.removeAllRanges();
                              selection.addRange(range);
                            }
                          } else {
                            inputRef.current!.innerText += text;
                          }
                        }

                        setHasText(!!inputRef.current?.innerText.trim());
                        setIsEmojiPickerOpen(false);
                      }}
                      perLine={7}
                    />
                  )}
                </div>
              </Suspense>
            </ErrorBoundary>
            <span
              ref={inputRef}
              contentEditable={!!isMounted}
              id="dm-input"
              placeholder={`Message ${chatId === 'me' ? 'RAS' : `${user?.firstName || ''}`}`}
              className={clsx(
                // `leading-6` makes each text line-box 24px tall — the same height as the
                // h-6 toolbar icons — so with the row's items-end a single line's text (and
                // the placeholder ::before) reads vertically centered instead of sitting low,
                // while multi-line growth just stacks 24px lines as before.
                'peer pointer-events-auto max-h-[75dvh] w-4 grow select-text flex-wrap overflow-y-hidden whitespace-pre-wrap break-words border-0 leading-6 text-zinc-800 empty:before:text-zinc-400 empty:before:content-[attr(placeholder)] focus:outline-hidden focus-visible:outline-hidden dark:text-zinc-200 dark:empty:before:text-zinc-500',
                sendMessageRequest.loading && 'animate-pulse',
              )}
              onInput={() => setHasText(!!inputRef.current?.innerText.trim())}
              onKeyDown={(event) => {
                if (event.key === 'Enter' && !event.shiftKey && inputRef.current) {
                  const platform = `${
                    typeof navigator !== 'undefined' &&
                    (navigator.userAgentData?.platform || navigator.platform)
                  }`;

                  const isIosOrAndroid =
                    platform.startsWith('i') || platform.toLowerCase().includes('android');

                  if (!isIosOrAndroid) {
                    event.preventDefault();
                    handleSend();

                    if (sendTypingTimer.current) {
                      clearTimeout(sendTypingTimer.current);
                    }

                    sendTypingRequest.run(
                      {
                        data: {
                          type: 'dm',
                          len: 0,
                        },
                      },
                      {
                        req: { input: `/api/v1/messageReceipt/realtime/typing/${chatIdNumber}` },
                        dontStoreError: true,
                        dontAlterState: true,
                      },
                    );
                  }
                } else {
                  if (sendTypingTimer.current) {
                    clearTimeout(sendTypingTimer.current);

                    sendTypingTimer.current = setTimeout(
                      () =>
                        sendTypingRequest.run(
                          {
                            data: {
                              type: 'dm',
                              len: inputRef.current?.innerText?.trim?.().length || 0,
                            },
                          },
                          {
                            req: {
                              input: `/api/v1/messageReceipt/realtime/typing/${chatIdNumber}`,
                            },
                            dontStoreError: true,
                            dontAlterState: true,
                          },
                        ),
                      250,
                    );
                  } else {
                    // first time typing send it almost right away
                    sendTypingTimer.current = setTimeout(
                      () =>
                        sendTypingRequest.run(
                          {
                            data: {
                              type: 'dm',
                              len: inputRef.current?.innerText?.trim?.().length || 0,
                            },
                          },
                          {
                            req: {
                              input: `/api/v1/messageReceipt/realtime/typing/${chatIdNumber}`,
                            },
                            dontStoreError: true,
                            dontAlterState: true,
                          },
                        ),
                      1,
                    );
                  }
                }
              }}
              onDragOver={(event) => {
                if (Array.from(event.dataTransfer.types || []).includes('Files')) {
                  event.preventDefault();
                }
              }}
              onDrop={(event) => {
                const file = imageFileFrom(event.dataTransfer?.files);
                if (file) {
                  event.preventDefault();
                  startUpload(file);
                }
              }}
              onPaste={(event) => {
                // An image on the clipboard is attached; otherwise fall back to
                // the plain-text paste behaviour (#1808).
                const file = imageFileFrom(event.clipboardData?.files);
                if (file) {
                  event.preventDefault();
                  startUpload(file);
                  return;
                }

                event.preventDefault();
                const input = event.target as HTMLElement;

                const text = event.clipboardData.getData('text/plain');

                if (document.queryCommandSupported?.('insertText')) {
                  document.execCommand('insertText', false, text);
                } else {
                  const range = document.getSelection()?.getRangeAt(0);

                  if (range) {
                    const textNode = document.createTextNode(text);

                    range.deleteContents();
                    range.insertNode(textNode);
                    range.selectNodeContents(textNode);
                    range.collapse(false);

                    const selection = window.getSelection();

                    if (selection) {
                      selection.removeAllRanges();
                      selection.addRange(range);
                    }
                  } else {
                    input.innerText += text;
                  }
                }
                setHasText(!!inputRef.current?.innerText.trim());
              }}
            />
            {/* Send button. On desktop you send with Enter and never tap this, so the
                icon is compact (h-5); on touch devices, where the tap IS the send, the
                shared `no-hover` variant bumps it back up to a comfortable target (h-6).
                Wrapped in a real <button> so it stays keyboard-focusable with an
                aria-label and a visible focus ring at both sizes. */}
            <button
              type="button"
              aria-label="Send message"
              disabled={!canSend}
              onClick={() => {
                if (canSend) {
                  handleSend();
                }
              }}
              className={clsx(
                'pointer-events-auto flex shrink-0 items-center justify-center self-end rounded-full transition focus:outline-hidden focus-visible:outline-2 focus-visible:outline-emerald-500 focus-visible:outline-offset-2',
                canSend ? 'cursor-pointer' : 'cursor-default',
              )}
            >
              <PaperAirplaneIcon
                aria-hidden="true"
                className={clsx(
                  'h-5 w-5 shrink-0 transition no-hover:h-6 no-hover:w-6',
                  canSend
                    ? 'text-emerald-500 hover:text-emerald-600 dark:text-emerald-400 dark:hover:text-emerald-500'
                    : 'text-zinc-300 dark:text-zinc-600',
                  (sendMessageRequest.loading || isUploading) && 'animate-pulse',
                )}
              />
            </button>
          </div>

          {/* Image preview — a thumbnail below the text, not inline (#1808).
              Shows a spinner overlay while uploading; hover ✕ removes it and the
              thumbnail opens a lightbox on click. */}
          {attachment && (
            <div className="pointer-events-auto mt-2 mb-1">
              <div className="relative inline-block">
                {attachment.status === 'done' && attachment.url && attachment.uploadedLoaded ? (
                  <ImageAttachment
                    url={attachment.url}
                    alt="Attachment preview"
                    onRemove={clearAttachment}
                  />
                ) : (
                  <div className="group/att relative inline-block">
                    <img
                      src={attachment.previewUrl}
                      alt="Attachment preview"
                      className={clsx(
                        'h-auto max-h-40 w-auto max-w-[12rem] rounded-lg object-cover ring-1 ring-zinc-900/10 dark:ring-white/10',
                        attachment.status === 'uploading' && 'opacity-60',
                        attachment.status === 'error' && 'opacity-40 grayscale',
                      )}
                    />
                    {attachment.status === 'uploading' && (
                      <div className="absolute inset-0 flex items-center justify-center">
                        <div
                          className="h-6 w-6 animate-spin rounded-full border-2 border-white/70 border-t-transparent"
                          aria-label="Uploading"
                        />
                      </div>
                    )}
                    <button
                      type="button"
                      onClick={clearAttachment}
                      title="Remove file"
                      aria-label="Remove file"
                      className="absolute -right-2 -top-2 rounded-full bg-zinc-900 p-1 text-white shadow-md ring-2 ring-white hover:bg-zinc-700 dark:ring-zinc-900"
                    >
                      <PlusIcon className="size-3.5 rotate-45" aria-hidden="true" />
                    </button>
                  </div>
                )}
                {/* Warm the served (R2) image off-screen while the local preview
                    above stays visible, then flip `uploadedLoaded` so the visible
                    <img> swaps to the served one only once it has decoded — no
                    blank frame between the spinner and the finished upload (#1822).
                    onError also flips it so a served-image failure still leaves the
                    (identical-looking) ImageAttachment in place rather than sticking
                    on the local preview forever. */}
                {attachment.status === 'done' && attachment.url && !attachment.uploadedLoaded && (
                  <img
                    src={attachment.url}
                    alt=""
                    aria-hidden="true"
                    className="hidden"
                    onLoad={() =>
                      setAttachment((prev) =>
                        prev?.id === attachment.id ? { ...prev, uploadedLoaded: true } : prev,
                      )
                    }
                    onError={() =>
                      setAttachment((prev) =>
                        prev?.id === attachment.id ? { ...prev, uploadedLoaded: true } : prev,
                      )
                    }
                  />
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
